import { ASCENDER, DESCENDER, GLYPH_STROKES, type Point, STEM_EM } from "./glyphStrokes";

export type Box = { width: number; height: number };
export type InkBox = { left: number; right: number; top: number; bottom: number };

/** Font measurements of the face the glyphs are actually drawn in, at the size they are drawn. */
export type GlyphMetrics = {
  /** Advance width of a run of text. */
  advance: (text: string) => number;
  /** Ink bounds of one character relative to its pen origin, y negative above the baseline. */
  inkBounds: (char: string) => InkBox | null;
};

export type TraceStroke = {
  charIndex: number;
  /** 1-based position of this stroke within its own letter, for the start-dot badge. */
  order: number;
  points: Point[];
  /** Cumulative length at each point, so the last entry is the stroke's length. */
  lengths: number[];
};

export type TraceLayout = {
  fontSize: number;
  /** Where the writing line sits in the board; the ruled line has to be drawn on it too. */
  baselineY: number;
  /** Width the word needs; wider than the board for a long phrase, which then scrolls. */
  contentWidth: number;
  strokes: TraceStroke[];
  /** Ink box per character, for scrolling the letter being traced into view. */
  ink: (InkBox | null)[];
  /** Characters with no skeleton fall back to the real glyph, drawn from the start at this pen x. */
  untraced: { char: string; x: number }[];
};

/**
 * Letter height as a share of the board's height, capped in dp: a tablet's taller board would
 * otherwise scale the stroke width, off-path tolerance and advance window along with it.
 * Deliberately independent of word length: a long phrase scrolls instead of shrinking.
 */
export const GLYPH_HEIGHT_RATIO = 0.9;
/** Sits above every phone's board, so only tablet-sized screens clamp. */
export const GLYPH_HEIGHT_MAX = 400;

export const traceFontSize = ({ height }: Box) =>
  Math.min(height * GLYPH_HEIGHT_RATIO, GLYPH_HEIGHT_MAX);

/**
 * Puts the writing line where the full ascender-to-descender band sits centred in the board, so a
 * letter that no longer fills a tall board is not left stranded near its bottom edge. Independent of
 * the word: the line must not jump because this one happens to have no descender.
 */
export const baselineFor = ({ height }: Box, fontSize: number) => {
  const above = (ASCENDER + STEM_EM / 2) * fontSize;
  const below = (STEM_EM / 2 - DESCENDER) * fontSize;
  return (height - above - below) / 2 + above;
};

/**
 * Air a pair of letters must keep between the ink they paint. The traced pen is one uniform width, so
 * a letter fills its ink box where the real glyph's terminals taper away: Andika's own gap for pairs
 * like "cy" is a third of a stem, which reads as one joined shape at tracing size.
 */
const MIN_LETTER_GAP = STEM_EM / 2;

/** The numbered pen dot follows the letter's size but caps out, so a big glyph isn't buried under it. */
export const penMarkerRadius = (fontSize: number) => Math.min(fontSize * 0.13, 16);

const emBounds = (strokes: Point[][]) => {
  const points = strokes.flat();
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  return {
    left: Math.min(...xs),
    right: Math.max(...xs),
    bottom: Math.min(...ys),
    top: Math.max(...ys),
  };
};

const cumulativeLengths = (points: Point[]) => {
  const lengths = [0];
  for (let i = 1; i < points.length; i++) {
    const previous = points[i - 1] as Point;
    const point = points[i] as Point;
    lengths.push(
      (lengths[i - 1] as number) + Math.hypot(point.x - previous.x, point.y - previous.y)
    );
  }
  return lengths;
};

/**
 * Places every stroke of the word in container pixels. The skeletons are authored as centre lines and
 * are what gets drawn, so each one is fitted to the font's measured ink box shrunk by half a pen width:
 * the traced letter then occupies the same footprint the real glyph would.
 */
export const layoutTrace = (
  text: string,
  box: Box,
  fontSize: number,
  metrics: GlyphMetrics
): TraceLayout => {
  const baselineY = baselineFor(box, fontSize);
  const inset = (STEM_EM / 2) * fontSize;
  const minGap = MIN_LETTER_GAP * fontSize;

  const strokes: TraceStroke[] = [];
  const ink: (InkBox | null)[] = [];
  const untraced: { char: string; x: number }[] = [];

  // A near-flat skeleton (l, i, the dash) carries no scale information in that axis, so it keeps em scale.
  const fit = (from: number, to: number) =>
    from < fontSize * 0.08 ? 1 : Math.min(1.25, Math.max(0.8, to / from));

  // Letters keep the font's own advances until the traced pen would crowd them; the extra space a
  // tight pair needs pushes the rest of the word along with it.
  let shift = 0;
  let previousRight = Number.NEGATIVE_INFINITY;

  [...text].forEach((char, charIndex) => {
    const bounds = metrics.inkBounds(char);
    if (!bounds || bounds.right <= bounds.left || bounds.bottom <= bounds.top) {
      ink.push(null);
      return;
    }

    const glyph = GLYPH_STROKES[char];
    const authored = glyph ? emBounds(glyph) : null;
    const authoredWidth = authored ? (authored.right - authored.left) * fontSize : 0;
    const authoredHeight = authored ? (authored.top - authored.bottom) * fontSize : 0;
    const targetWidth = Math.max(0, bounds.right - bounds.left - 2 * inset);
    const targetHeight = Math.max(0, bounds.bottom - bounds.top - 2 * inset);
    const scaleX = fit(authoredWidth, targetWidth);
    const scaleY = fit(authoredHeight, targetHeight);

    // What the letter actually paints, measured from its ink box centre: the skeleton span plus the
    // pen's two half widths, or the real glyph's own box for a character with no skeleton.
    const halfWidth = authored
      ? (authoredWidth * scaleX) / 2 + inset
      : (bounds.right - bounds.left) / 2;

    let centerX =
      metrics.advance(text.slice(0, charIndex)) + (bounds.left + bounds.right) / 2 + shift;
    const crowding = previousRight + minGap - (centerX - halfWidth);
    if (crowding > 0) {
      shift += crowding;
      centerX += crowding;
    }
    previousRight = centerX + halfWidth;

    const penX = centerX - (bounds.left + bounds.right) / 2;
    const inkBox: InkBox = {
      left: penX + bounds.left,
      right: penX + bounds.right,
      top: baselineY + bounds.top,
      bottom: baselineY + bounds.bottom,
    };
    ink.push(inkBox);

    if (!glyph || !authored) {
      untraced.push({ char, x: penX });
      return;
    }

    const centerY = (inkBox.top + inkBox.bottom) / 2;
    const authoredCenterX = (authored.left + authored.right) / 2;
    const authoredCenterY = (authored.top + authored.bottom) / 2;

    glyph.forEach((stroke, order) => {
      const points = stroke.map((point) => ({
        x: centerX + (point.x - authoredCenterX) * fontSize * scaleX,
        y: centerY - (point.y - authoredCenterY) * fontSize * scaleY,
      }));
      strokes.push({ charIndex, order: order + 1, points, lengths: cumulativeLengths(points) });
    });
  });

  // Centring needs the width the nudges ended up at, so the word is laid out from zero and slid after.
  // A word that fits stays centred and still; a longer one gets half a board of slack on each side,
  // which is what lets every letter sit dead centre as the word scrolls past.
  const wordWidth = metrics.advance(text) + shift;
  const contentWidth = wordWidth <= box.width ? box.width : wordWidth + box.width;
  const startX = (contentWidth - wordWidth) / 2;

  for (const stroke of strokes) {
    for (const point of stroke.points) point.x += startX;
  }
  for (const inkBox of ink) {
    if (inkBox) {
      inkBox.left += startX;
      inkBox.right += startX;
    }
  }
  for (const item of untraced) item.x += startX;

  return { fontSize, baselineY, contentWidth, strokes, ink, untraced };
};

export const strokeLength = (stroke: TraceStroke) => stroke.lengths.at(-1) ?? 0;

/**
 * How far the word is shifted left so the letter being traced sits dead centre. The student never
 * pans by hand — that would fight the tracing gesture — and the shift stops at the ends of the word,
 * so a word narrower than the board never moves at all.
 */
export const traceScrollOffset = (
  letter: Pick<InkBox, "left" | "right">,
  viewportWidth: number,
  contentWidth: number
) => {
  const centred = (letter.left + letter.right) / 2 - viewportWidth / 2;
  return Math.min(Math.max(centred, 0), Math.max(0, contentWidth - viewportWidth));
};

const interpolate = (stroke: TraceStroke, index: number, within: number) => {
  const from = stroke.points[index] as Point;
  const to = stroke.points[index + 1];
  if (!to) return from;

  const segment = (stroke.lengths[index + 1] as number) - (stroke.lengths[index] as number);
  const ratio = segment > 0 ? within / segment : 0;
  return { x: from.x + (to.x - from.x) * ratio, y: from.y + (to.y - from.y) * ratio };
};

const segmentAt = (stroke: TraceStroke, length: number) => {
  for (let i = stroke.lengths.length - 2; i >= 0; i--) {
    if (length >= (stroke.lengths[i] as number)) return i;
  }
  return 0;
};

export const pointAt = (stroke: TraceStroke, length: number): Point => {
  const index = segmentAt(stroke, length);
  return interpolate(stroke, index, length - (stroke.lengths[index] as number));
};

/** The stroke's path from its start up to `length`, for the reveal mask. */
export const pointsUpTo = (stroke: TraceStroke, length: number): Point[] => {
  const index = segmentAt(stroke, length);
  return [...stroke.points.slice(0, index + 1), pointAt(stroke, length)];
};

/** The stroke's path from `length` to its end, as the child's direction hint. */
export const pointsFrom = (stroke: TraceStroke, length: number): Point[] => [
  pointAt(stroke, length),
  ...stroke.points.filter((_, i) => (stroke.lengths[i] as number) > length),
];

/**
 * How far along the stroke the finger has reached, searching only `window` px ahead of `from` so the
 * pen can neither skip a loop nor run backwards. Returns null when the finger is off the path.
 */
export const advanceAlong = (
  stroke: TraceStroke,
  point: Point,
  from: number,
  window: number,
  tolerance: number
): number | null => {
  let reached: number | null = null;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (let i = 0; i < stroke.points.length - 1; i++) {
    const segmentStart = stroke.lengths[i] as number;
    const segmentEnd = stroke.lengths[i + 1] as number;
    if (segmentEnd < from) continue;
    if (segmentStart > from + window) break;

    const a = stroke.points[i] as Point;
    const b = stroke.points[i + 1] as Point;
    const span = segmentEnd - segmentStart;
    if (!span) continue;

    const ratio = Math.min(
      1,
      Math.max(0, ((point.x - a.x) * (b.x - a.x) + (point.y - a.y) * (b.y - a.y)) / span ** 2)
    );
    const closest = { x: a.x + (b.x - a.x) * ratio, y: a.y + (b.y - a.y) * ratio };
    const at = segmentStart + span * ratio;
    const distance = Math.hypot(point.x - closest.x, point.y - closest.y);

    if (at < from || distance > tolerance) continue;
    // Nearest wins, so a finger near a corner can't cut across to the segment after it.
    if (distance < closestDistance || (distance === closestDistance && at > (reached ?? 0))) {
      closestDistance = distance;
      reached = at;
    }
  }

  // One move may not carry the pen further than the window, so a flick can't finish a long stroke.
  return reached === null ? null : Math.min(reached, from + window);
};
